/**
 * Application router.
 *
 * - `/login` renders bare (no shell).
 * - Every other route renders inside `AppShellLayout` so they all
 *   share the navbar + header.
 * - Module routes are placeholders until those phases land.
 */

import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AppShellLayout } from "./AppShellLayout";
import {
  KeepStateRedirect,
  LegacySolutionSettingsRedirect,
  LegacyTicketRedirect,
} from "./legacyRedirects";
import { Login } from "@/features/auth/Login";
import { HomePage } from "@/features/home/HomePage";
import { RequireAgent } from "@/features/auth/RequireAgent";
import { RequireAuth } from "@/features/auth/RequireAuth";
import { RequireRole } from "@/features/auth/RequireRole";
import { RolePreviewProvider } from "@/features/auth/RolePreviewProvider";
import { TICKETS_PATH } from "@/features/admin/ticketsPath";
import { CUSTOMER_DOCUMENTS_PATH, RELEASE_NOTES_PATH } from "@/features/documentation/paths";
import { HELP_SUPPORT_NEW_PATH, HELP_SUPPORT_PATH } from "@/features/help-support/helpSupportPath";
import {
  SolutionPlaceholderPage,
  type SolutionPlaceholderSlug,
} from "@/features/modules/SolutionPlaceholders";

// Pages loaded when they are first opened (ADO 2544): sign-in and Home need
// only the shell, so the heavy pages — the pipeline editor, archive,
// administration, release validation — are not downloaded up front. The
// shell shows a loader while a page's code arrives (`AppShellLayout`).
const ValidationSettingsPage = lazy(() =>
  import("@/features/validation/settings/ValidationSettingsPage").then((m) => ({
    default: m.ValidationSettingsPage,
  })),
);
const AuditViewer = lazy(() =>
  import("@/features/admin/AuditViewer").then((m) => ({ default: m.AuditViewer })),
);
const SolutionAccessPage = lazy(() =>
  import("@/features/admin/SolutionAccessPage").then((m) => ({ default: m.SolutionAccessPage })),
);
const TicketDetail = lazy(() =>
  import("@/features/admin/TicketDetail").then((m) => ({ default: m.TicketDetail })),
);
const TicketsList = lazy(() =>
  import("@/features/admin/TicketsList").then((m) => ({ default: m.TicketsList })),
);
const ReleaseNotesPage = lazy(() =>
  import("@/features/documentation/ReleaseNotesPage").then((m) => ({
    default: m.ReleaseNotesPage,
  })),
);
const CustomerDocumentsPage = lazy(() =>
  import("@/features/documentation/CustomerDocumentsPage").then((m) => ({
    default: m.CustomerDocumentsPage,
  })),
);
const HelpSupportList = lazy(() =>
  import("@/features/help-support/HelpSupportList").then((m) => ({ default: m.HelpSupportList })),
);
const HelpSupportNew = lazy(() =>
  import("@/features/help-support/HelpSupportNew").then((m) => ({ default: m.HelpSupportNew })),
);
const HelpSupportTicketPage = lazy(() =>
  import("@/features/help-support/HelpSupportTicketPage").then((m) => ({
    default: m.HelpSupportTicketPage,
  })),
);
const ProvisionUser = lazy(() =>
  import("@/features/admin/ProvisionUser").then((m) => ({ default: m.ProvisionUser })),
);
const UserDetail = lazy(() =>
  import("@/features/admin/UserDetail").then((m) => ({ default: m.UserDetail })),
);
const UsersList = lazy(() =>
  import("@/features/admin/UsersList").then((m) => ({ default: m.UsersList })),
);
const ArchiveContainerDetailPage = lazy(() =>
  import("@/features/archive/ArchiveContainerDetailPage").then((m) => ({
    default: m.ArchiveContainerDetailPage,
  })),
);
const ArchiveContainersPage = lazy(() =>
  import("@/features/archive/ArchiveContainersPage").then((m) => ({
    default: m.ArchiveContainersPage,
  })),
);
const ArchiveDatasetsPage = lazy(() =>
  import("@/features/archive/ArchiveDatasetsPage").then((m) => ({
    default: m.ArchiveDatasetsPage,
  })),
);
const ArchiveSearchPage = lazy(() =>
  import("@/features/archive/ArchiveSearchPage").then((m) => ({ default: m.ArchiveSearchPage })),
);
const NewArchiveContainerPage = lazy(() =>
  import("@/features/archive/NewArchiveContainerPage").then((m) => ({
    default: m.NewArchiveContainerPage,
  })),
);
const VeevaApproval = lazy(() =>
  import("@/features/admin/VeevaApproval").then((m) => ({ default: m.VeevaApproval })),
);
const SecondLineSupport = lazy(() =>
  import("@/features/solutions/validation/second-line-support").then((m) => ({
    default: m.SecondLineSupport,
  })),
);
const BlobBrowser = lazy(() =>
  import("@/features/integration/BlobBrowser").then((m) => ({ default: m.BlobBrowser })),
);
const CaraBrowser = lazy(() =>
  import("@/features/integration/CaraBrowser").then((m) => ({ default: m.CaraBrowser })),
);
const ConnectionDetail = lazy(() =>
  import("@/features/integration/ConnectionDetail").then((m) => ({ default: m.ConnectionDetail })),
);
const IntegrationsHub = lazy(() =>
  import("@/features/integration/IntegrationsHub").then((m) => ({ default: m.IntegrationsHub })),
);
const NewConnection = lazy(() =>
  import("@/features/integration/NewConnection").then((m) => ({ default: m.NewConnection })),
);
const S3Browser = lazy(() =>
  import("@/features/integration/S3Browser").then((m) => ({ default: m.S3Browser })),
);
const SharePointBrowser = lazy(() =>
  import("@/features/integration/SharePointBrowser").then((m) => ({
    default: m.SharePointBrowser,
  })),
);
const ConnectionBrowserPage = lazy(() =>
  import("@/features/browse/ConnectionBrowserPage").then((m) => ({
    default: m.ConnectionBrowserPage,
  })),
);
const CollectionDetail = lazy(() =>
  import("@/features/collections/CollectionDetail").then((m) => ({ default: m.CollectionDetail })),
);
const CollectionsList = lazy(() =>
  import("@/features/collections/CollectionsList").then((m) => ({ default: m.CollectionsList })),
);
const NewCollection = lazy(() =>
  import("@/features/collections/NewCollection").then((m) => ({ default: m.NewCollection })),
);
const DatabaseBrowser = lazy(() =>
  import("@/features/integration/DatabaseBrowser").then((m) => ({ default: m.DatabaseBrowser })),
);
const VeevaBrowser = lazy(() =>
  import("@/features/integration/VeevaBrowser").then((m) => ({ default: m.VeevaBrowser })),
);
const NotificationsPage = lazy(() =>
  import("@/features/notifications/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const CreatePicker = lazy(() =>
  import("@/features/solutions/create/CreatePicker").then((m) => ({ default: m.CreatePicker })),
);
const TranslatePage = lazy(() =>
  import("@/features/solutions/create/translate/TranslatePage").then((m) => ({
    default: m.TranslatePage,
  })),
);
const CreateQaPage = lazy(() =>
  import("@/features/solutions/create/qa/CreateQaPage").then((m) => ({ default: m.CreateQaPage })),
);
const ExploreChat = lazy(() =>
  import("@/features/solutions/explore").then((m) => ({ default: m.ExploreChat })),
);
const ExploreList = lazy(() =>
  import("@/features/solutions/explore").then((m) => ({ default: m.ExploreList })),
);
const PipelineCanvas = lazy(() =>
  import("@/features/pipelines/PipelineCanvas").then((m) => ({ default: m.PipelineCanvas })),
);
const NewPipeline = lazy(() =>
  import("@/features/projects/NewPipeline").then((m) => ({ default: m.NewPipeline })),
);
const NewProject = lazy(() =>
  import("@/features/projects/NewProject").then((m) => ({ default: m.NewProject })),
);
const ProjectDetail = lazy(() =>
  import("@/features/projects/ProjectDetail").then((m) => ({ default: m.ProjectDetail })),
);
const ProjectsHub = lazy(() =>
  import("@/features/projects/ProjectsHub").then((m) => ({ default: m.ProjectsHub })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const Me = lazy(() => import("@/features/users/Me").then((m) => ({ default: m.Me })));
const QualityPage = lazy(() =>
  import("@/features/solutions/quality").then((m) => ({ default: m.QualityPage })),
);
const AssignmentsPage = lazy(() =>
  import("@/features/solutions/clinical/assignments/AssignmentsPage").then((m) => ({
    default: m.AssignmentsPage,
  })),
);
const EtmfCompletenessPage = lazy(() =>
  import("@/features/solutions/clinical/etmf-completeness/EtmfCompletenessPage").then((m) => ({
    default: m.EtmfCompletenessPage,
  })),
);
const EtmfIntakePage = lazy(() =>
  import("@/features/solutions/clinical/EtmfIntakePage").then((m) => ({
    default: m.EtmfIntakePage,
  })),
);
const ReviewPage = lazy(() =>
  import("@/features/solutions/review").then((m) => ({ default: m.ReviewPage })),
);
const MyTests = lazy(() =>
  import("@/features/validation/MyTests").then((m) => ({ default: m.MyTests })),
);
const NewRun = lazy(() =>
  import("@/features/validation/NewRun").then((m) => ({ default: m.NewRun })),
);
const RunDetail = lazy(() =>
  import("@/features/validation/RunDetail").then((m) => ({ default: m.RunDetail })),
);
const ValidationHub = lazy(() =>
  import("@/features/validation/ValidationHub").then((m) => ({ default: m.ValidationHub })),
);

/** Redirects an old `/integration/<type>/<id>` browse URL to its new home under
 *  `/browse`. Browsing moved out of Integrations, but links of the old shape
 *  may still exist in bookmarks, notes and chat history. */
/** Solutions routes that show a placeholder until their page lands. Every one
 *  is open to all signed-in roles (ADR-0018); the paths are final, so the real
 *  page replaces the element without moving the URL. */
const SOLUTION_PLACEHOLDER_ROUTES: { path: string; slug: SolutionPlaceholderSlug }[] = [];

/** `/ai/chats/:chatId` → the same chat in Explore. Chats became Explore
 *  (todo-demo-08); old links in bookmarks and notes keep working. */
function LegacyChatRedirect() {
  const { chatId = "" } = useParams();
  return <Navigate to={`/solutions/explore/${chatId}`} replace />;
}

/** Keeps the query (`?assessment=<id>`) — a bare Navigate would drop it. */
function LegacyRiskAssessmentRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/validation/risk-assessment${search}`} replace />;
}

function LegacyBrowseRedirect({ sourceType }: { sourceType: string }) {
  const { connectionId = "" } = useParams();
  return <Navigate to={`/browse/${sourceType}/${connectionId}`} replace />;
}

export function AppRoutes() {
  return (
    // Above every RequireRole and AppShellLayout so the "view as role" preview
    // is shared by the route guards AND the nav, and survives navigation
    // (a per-layout provider would reset it on each page change).
    <RolePreviewProvider>
      {/* For a page opened outside the shell; inside it, `AppShellLayout`
          shows its own loader and keeps the sidebar and header. */}
      <Suspense fallback={null}>
        <Routes>
          {/* Home (ADR-0070): what is waiting on you and where you left off. */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <HomePage />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route path="/login" element={<Login />} />

          <Route
            path="/me"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <Me />
                </AppShellLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/settings"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <SettingsPage />
                </AppShellLayout>
              </RequireAuth>
            }
          />

          {/* The full list behind the header bell. Any signed-in user may open
            it — the notifications themselves are scoped by what the underlying
            read returns, so someone without pipelines simply sees none. */}
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <NotificationsPage />
                </AppShellLayout>
              </RequireAuth>
            }
          />

          <Route
            path="/admin/users"
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <UsersList />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/admin/users/new"
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <ProvisionUser />
                </AppShellLayout>
              </RequireRole>
            }
          />
          {/* Support tickets: Administration → Tickets (ADR-0076), Administrators
            only; the backend checks `ticket.admin_read` / `ticket.admin_update`.
            The Validation-group addresses of ADR-0069 — and links in emails
            already sent — keep working. */}
          {[TICKETS_PATH, `${TICKETS_PATH}/:ticketId`].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireRole role="administrator">
                  <AppShellLayout>
                    {path === TICKETS_PATH ? <TicketsList /> : <TicketDetail />}
                  </AppShellLayout>
                </RequireRole>
              }
            />
          ))}
          <Route
            path="/solutions/validation/tickets"
            element={<Navigate to={TICKETS_PATH} replace />}
          />
          <Route
            path="/solutions/validation/tickets/:ticketId"
            element={<LegacyTicketRedirect />}
          />
          {/* Documentation (ADR-0080): every signed-in role; the backend checks
            `documentation.read`. The Administration → Qualification package
            address of ADR-0076 and the old Documentation placeholder open the
            release notes. */}
          <Route
            path={RELEASE_NOTES_PATH}
            element={
              <RequireAuth>
                <AppShellLayout>
                  <ReleaseNotesPage />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route
            path={CUSTOMER_DOCUMENTS_PATH}
            element={
              <RequireAuth>
                <AppShellLayout>
                  <CustomerDocumentsPage />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/admin/qualification-package"
            element={<Navigate to={RELEASE_NOTES_PATH} replace />}
          />
          <Route
            path="/admin/documentation"
            element={<Navigate to={RELEASE_NOTES_PATH} replace />}
          />
          <Route
            path="/admin/approvals/veeva/:changeId"
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <VeevaApproval />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <UserDetail />
                </AppShellLayout>
              </RequireRole>
            }
          />
          {/* Who may use each Solution and agent (ADR-0048): Administrators
            only (ADR-0068); the backend checks `solution.grant`. */}
          <Route
            path="/admin/solution-access"
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <SolutionAccessPage />
                </AppShellLayout>
              </RequireRole>
            }
          />
          {/* The release validation settings opened from Administration before
            ADR-0064; the bookmark keeps working. */}
          <Route
            path="/admin/validation-settings"
            element={<KeepStateRedirect to="/validation/settings" />}
          />
          <Route
            path="/admin/audit"
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <AuditViewer />
                </AppShellLayout>
              </RequireRole>
            }
          />

          {/* Help & support (ADR-0093): tickets to DAQUMA itself, Administrators
            only; the backend checks `help_support.read` / `help_support.write`. */}
          <Route
            path={HELP_SUPPORT_PATH}
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <HelpSupportList />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path={HELP_SUPPORT_NEW_PATH}
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <HelpSupportNew />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path={`${HELP_SUPPORT_PATH}/:ticketId`}
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <HelpSupportTicketPage />
                </AppShellLayout>
              </RequireRole>
            }
          />

          {/* The hub's three views are tabs, but each keeps its own URL so it can
            be linked to and landed on. The guards are per-route and unchanged:
            Connected and Add connection are Developer+, Roles is any signed-in
            user — the hub hides the tabs a viewer's role would be refused. */}
          <Route
            path="/integration/connections"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <IntegrationsHub tab="connected" />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/integration/bindings"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <IntegrationsHub tab="roles" />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/integration/available"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <IntegrationsHub tab="available" />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/integration/connections/new"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <NewConnection />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/integration/connections/:connectionId"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <ConnectionDetail />
                </AppShellLayout>
              </RequireRole>
            }
          />
          {/* Browsing is its own destination (left nav), with the connection as a
            dropdown inside it. `:connectionId?` is optional so the nav link can
            land on the picker; selecting one fills it in. */}
          <Route
            path="/browse/veeva/:connectionId?"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <ConnectionBrowserPage
                    sourceType="veeva"
                    title="Veeva Vault"
                    description="Query a vault with VQL, or browse its File Staging (FTP) area."
                  >
                    {(id) => <VeevaBrowser connectionId={id} />}
                  </ConnectionBrowserPage>
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/browse/cara/:connectionId?"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <ConnectionBrowserPage
                    sourceType="cara"
                    title="Generis CARA"
                    description="Search a CARA tenant's records. Read-only — nothing here writes to CARA."
                  >
                    {(id) => <CaraBrowser connectionId={id} />}
                  </ConnectionBrowserPage>
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/browse/blob/:connectionId?"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <ConnectionBrowserPage
                    sourceType="blob"
                    title="Azure Blob Storage"
                    description="Browse containers and files, upload, and download."
                  >
                    {(id) => <BlobBrowser connectionId={id} />}
                  </ConnectionBrowserPage>
                </AppShellLayout>
              </RequireRole>
            }
          />

          <Route
            path="/browse/database/:connectionId?"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <ConnectionBrowserPage
                    sourceType="database"
                    title="Database"
                    description="Browse a connected database's schemas and tables, and preview rows. Read-only."
                  >
                    {(id) => <DatabaseBrowser connectionId={id} />}
                  </ConnectionBrowserPage>
                </AppShellLayout>
              </RequireRole>
            }
          />

          <Route
            path="/browse/s3/:connectionId?"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <ConnectionBrowserPage
                    sourceType="s3"
                    title="AWS S3"
                    description="Browse buckets and objects, upload, and download."
                  >
                    {(id) => <S3Browser connectionId={id} />}
                  </ConnectionBrowserPage>
                </AppShellLayout>
              </RequireRole>
            }
          />

          <Route
            path="/browse/sharepoint/:connectionId?"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <ConnectionBrowserPage
                    sourceType="sharepoint"
                    title="SharePoint"
                    description="Browse a site's document libraries, download, and — on a read-write connection — upload."
                  >
                    {(id) => <SharePointBrowser connectionId={id} />}
                  </ConnectionBrowserPage>
                </AppShellLayout>
              </RequireRole>
            }
          />

          {/* The old per-connection browse URLs. Kept as redirects so existing
            bookmarks and any link still pointing at them land in the right
            place rather than on a blank route. */}
          <Route
            path="/integration/veeva/:connectionId"
            element={<LegacyBrowseRedirect sourceType="veeva" />}
          />
          <Route
            path="/integration/blob/:connectionId"
            element={<LegacyBrowseRedirect sourceType="blob" />}
          />

          {/* Solutions → Archive. Search first: for an end user the archive is
            something you search, not an inventory you browse; the container
            list is the records manager's view. Every role may enter (the
            `User` role holds archive.search / read / retrieve) — which archive
            a person can actually open is decided per container by a grant,
            server-side. Creating a container is an Administrator act. */}
          <Route
            path="/archive"
            element={
              <RequireAgent agent="archive_search">
                <AppShellLayout>
                  <ArchiveSearchPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/archive/search"
            element={
              <RequireAgent agent="archive_search">
                <AppShellLayout>
                  <ArchiveSearchPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/archive/containers"
            element={
              <RequireAgent agent="archive_containers">
                <AppShellLayout>
                  <ArchiveContainersPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          {/* Before `/archive/containers/:containerId`, or "new" reads as an id. */}
          <Route
            path="/archive/containers/new"
            element={
              <RequireRole role="administrator">
                <AppShellLayout>
                  <NewArchiveContainerPage />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/archive/containers/:containerId"
            element={
              <RequireAgent agent="archive_containers">
                <AppShellLayout>
                  <ArchiveContainerDetailPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/archive/containers/:containerId/datasets"
            element={
              <RequireAgent agent="archive_containers">
                <AppShellLayout>
                  <ArchiveDatasetsPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />

          {/* Approvals moved under Projects. Notifications already sent carry the
            old link, so it redirects rather than 404s. */}
          <Route path="/approvals" element={<Navigate to="/projects/approvals" replace />} />
          <Route
            path="/projects"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <ProjectsHub tab="projects" />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          {/* Before `/projects/:projectId`, or "approvals" reads as a project id. */}
          <Route
            path="/projects/approvals"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <ProjectsHub tab="approvals" />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/projects/new"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <NewProject />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:projectId"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <ProjectDetail />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:projectId/pipelines/new"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <NewPipeline />
                </AppShellLayout>
              </RequireAuth>
            }
          />
          <Route
            path="/projects/:projectId/pipelines/:pipelineId"
            element={
              <RequireAuth>
                <AppShellLayout>
                  <PipelineCanvas />
                </AppShellLayout>
              </RequireAuth>
            }
          />

          {/* Create — a type picker, then one page per type (Translate, Q&A).
            Every role (ADR-0018); the backend decides which collections. */}
          <Route
            path="/solutions/create"
            element={
              <RequireAgent agent="create">
                <AppShellLayout>
                  <CreatePicker />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/solutions/create/translate"
            element={
              <RequireAgent agent="create">
                <AppShellLayout>
                  <TranslatePage />
                </AppShellLayout>
              </RequireAgent>
            }
          />

          {/* Create → Q&A — answer a questionnaire from collections, every role (ADR-0018). */}
          <Route
            path="/solutions/create/qa"
            element={
              <RequireAgent agent="create">
                <AppShellLayout>
                  <CreateQaPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />

          {/* Review — the statement / data checker, every role (ADR-0018). */}
          <Route
            path="/solutions/review"
            element={
              <RequireAgent agent="review">
                <AppShellLayout>
                  <ReviewPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />

          {/* Quality — the ALCOA+ document scorecard, every role (ADR-0018). */}
          <Route
            path="/solutions/quality"
            element={
              <RequireAgent agent="quality">
                <AppShellLayout>
                  <QualityPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />

          {/* Clinical → eTMF intake — results of the pipelines published to it,
            every role (ADR-0032). */}
          <Route
            path="/solutions/clinical/etmf-intake"
            element={
              <RequireAgent agent="etmf_intake">
                <AppShellLayout>
                  <EtmfIntakePage />
                </AppShellLayout>
              </RequireAgent>
            }
          />

          {/* Clinical → eTMF completeness — sites × TMF zones from a Veeva vault
            the user may use, every role (ADR-0035). */}
          <Route
            path="/solutions/clinical/etmf-completeness"
            element={
              <RequireAgent agent="etmf_completeness">
                <AppShellLayout>
                  <EtmfCompletenessPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/solutions/clinical/assignments"
            element={
              <RequireAgent agent="etmf_assignments">
                <AppShellLayout>
                  <AssignmentsPage />
                </AppShellLayout>
              </RequireAgent>
            }
          />

          {SOLUTION_PLACEHOLDER_ROUTES.map(({ path, slug }) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAuth>
                  <AppShellLayout>
                    <SolutionPlaceholderPage slug={slug} />
                  </AppShellLayout>
                </RequireAuth>
              }
            />
          ))}

          {/* Collections (the UI name for document indexes) and the Administration
            placeholder keep the gating of the section they sit in: Developer+,
            like Integrations and the audit log. The backend enforces ownership
            and grants regardless. */}
          <Route
            path="/development/collections"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <CollectionsList />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/development/collections/new"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <NewCollection />
                </AppShellLayout>
              </RequireRole>
            }
          />
          <Route
            path="/development/collections/:indexId"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <AppShellLayout>
                  <CollectionDetail />
                </AppShellLayout>
              </RequireRole>
            }
          />
          {/* Explore — grounded chats over collections, every role (ADR-0018).
            The old /ai/chats routes redirect here. */}
          <Route
            path="/solutions/explore"
            element={
              <RequireAgent agent="explore">
                <AppShellLayout>
                  <ExploreList />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/solutions/explore/:chatId"
            element={
              <RequireAgent agent="explore">
                <AppShellLayout>
                  <ExploreChat />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          {/* Validation › 1st line support — Explore plus tickets (same components,
            `surface="support"`; only these chats can raise a ticket). */}
          <Route
            path="/solutions/validation/first-line-support"
            element={
              <RequireAgent agent="first_line_support">
                <AppShellLayout>
                  <ExploreList surface="support" />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/solutions/validation/first-line-support/:chatId"
            element={
              <RequireAgent agent="first_line_support">
                <AppShellLayout>
                  <ExploreChat surface="support" />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          {[
            "/solutions/validation/second-line-support",
            "/solutions/validation/second-line-support/:chatId",
          ].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequireAgent agent="second_line_support">
                  <AppShellLayout>
                    <SecondLineSupport />
                  </AppShellLayout>
                </RequireAgent>
              }
            />
          ))}
          <Route path="/ai/chats" element={<Navigate to="/solutions/explore" replace />} />
          <Route path="/ai/chats/:chatId" element={<LegacyChatRedirect />} />
          {/* The assessment moved under Validation, where it is the journey's
          first stage. The old path keeps working — links to it are out there. */}
          <Route path="/ai/risk-assessment" element={<LegacyRiskAssessmentRedirect />} />
          {/* Release validation (ADR-0019): the runs and the risk assessment are
          two destinations under Solutions → Validation. Opening a run is
          Developer+; the list and a run are every role's, since anyone may be
          named on a run (ADR-0054) and the API scopes what they see;
          the risk assessment and "My tests" are every role — a tester is any
          Entra user. */}
          <Route
            path="/validation/runs"
            element={
              <RequireAgent agent="release_validation">
                <AppShellLayout>
                  <ValidationHub tab="runs" />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          <Route
            path="/validation/risk-assessment"
            element={
              <RequireAgent agent="release_validation">
                <AppShellLayout>
                  <ValidationHub tab="risk-assessment" />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          {/* The Solution settings pages of ADR-0067 are gone (ADR-0069); their
            addresses — ticket emails among them — lead to where each part went. */}
          {[
            "/solutions/:solution/settings",
            "/solutions/:solution/settings/:tab",
            "/solutions/:solution/settings/:tab/:itemId",
          ].map((path) => (
            <Route key={path} path={path} element={<LegacySolutionSettingsRedirect />} />
          ))}
          {/* The release validation settings (ADR-0058, ADR-0064, ADR-0069): opened
            from the gear on the runs page; Administrator only — the backend
            checks `solution_defaults.update` and the release_validation agent. */}
          <Route
            path="/validation/settings"
            element={
              <RequireRole role="administrator">
                <RequireAgent agent="release_validation">
                  <AppShellLayout>
                    <ValidationSettingsPage />
                  </AppShellLayout>
                </RequireAgent>
              </RequireRole>
            }
          />
          <Route
            path="/validation/runs/new"
            element={
              <RequireRole role={["developer", "administrator"]}>
                <RequireAgent agent="release_validation">
                  <AppShellLayout>
                    <NewRun />
                  </AppShellLayout>
                </RequireAgent>
              </RequireRole>
            }
          />
          <Route
            path="/validation/runs/:runId"
            element={
              <RequireAgent agent="release_validation">
                <AppShellLayout>
                  <RunDetail />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          {/* The Solutions-tree placeholder became the real page. */}
          <Route
            path="/solutions/validation/my-tests"
            element={<Navigate to="/validation/my-tests" replace />}
          />
          <Route
            path="/validation/my-tests"
            element={
              <RequireAgent agent="my_tests">
                <AppShellLayout>
                  <MyTests />
                </AppShellLayout>
              </RequireAgent>
            }
          />
          {/* The /modules/ai-agents and /modules/ai-tools placeholders are
          superseded by the real /ai/* routes above; the paths keep working for
          anyone holding a bookmark. */}
          <Route path="/modules/ai-agents" element={<Navigate to="/solutions/explore" replace />} />
          <Route
            path="/modules/ai-tools"
            element={<Navigate to="/validation/risk-assessment" replace />}
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </RolePreviewProvider>
  );
}
